/**
 * Header de la aplicación con look Quantia:
 *  - Fondo negro premium (#0D0D0D)
 *  - Logo QUANT·IA + Licitator
 *  - Navegación con botones outline-light, activo en lime
 *  - Botón de salida discreto (outline-light)
 *
 * Se oculta en /login (que ya usa su propio hero oscuro).
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Box, Button, Container } from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

import { Logo } from './quantia';
import { COLORS } from '../theme/theme';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { path: '/',         label: 'Inicio',          icon: <HomeIcon /> },
  { path: '/analizar', label: 'Analizar',        icon: <SearchIcon /> },
  { path: '/generar',  label: 'Generar Oferta',  icon: <DescriptionIcon /> },
];

const baseNavStyles = {
  fontFamily: "'Fira Sans', sans-serif",
  fontWeight: 700,
  fontSize: '0.875rem',
  textTransform: 'none',
  borderRadius: 12,
  borderWidth: 1.5,
  borderStyle: 'solid',
  borderColor: 'rgba(255, 255, 255, 0.12)',
  color: 'rgba(255, 255, 255, 0.85)',
  px: 2.25,
  py: 0.85,
  letterSpacing: '0.01em',
  transition: 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)',
};

const activeNavStyles = {
  borderColor: COLORS.accent,
  color: COLORS.accent,
  background: 'rgba(194, 229, 58, 0.08)',
  '&:hover': {
    background: 'rgba(194, 229, 58, 0.14)',
    borderColor: COLORS.accent,
  },
};

const inactiveNavStyles = {
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.45)',
    background: 'rgba(255, 255, 255, 0.04)',
    color: '#FFFFFF',
  },
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (location.pathname === '/login') {
    return null;
  }

  const isActive = (path) => location.pathname === path;

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: COLORS.black,
        boxShadow: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        mb: 0,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 64, md: 76 },
            gap: 2,
          }}
        >
          {/* Logo + bajada producto */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Logo onClick={() => navigate('/')} />
          </Box>

          {/* Navegación (solo si está autenticado) */}
          {isAuthenticated && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                  sx={{
                    ...baseNavStyles,
                    ...(isActive(item.path) ? activeNavStyles : inactiveNavStyles),
                  }}
                >
                  {item.label}
                </Button>
              ))}

              <Button
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  ...baseNavStyles,
                  ml: 1,
                  borderColor: 'rgba(239, 68, 68, 0.45)',
                  color: 'rgba(239, 68, 68, 0.95)',
                  '&:hover': {
                    borderColor: '#EF4444',
                    background: 'rgba(239, 68, 68, 0.08)',
                    color: '#EF4444',
                  },
                }}
              >
                Salir
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
